import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, X, Download, Copy as CopyIcon } from 'lucide-react';
import { usersAPI, walletAPI } from '../../../services/api';
import { toast } from 'sonner';

const UserDetailsModal = ({
  isOpen,
  onClose,
  selectedUser,
  getUserDisplayName,
  getStatusBadge,
  getDocumentDisplayName
}) => {
  // Fetch wallets only when modal open & user present
  const {
    data: walletsData,
    isLoading: walletsLoading,
    isError: walletsError,
    error: walletsErrorObj
  } = useQuery({
    queryKey: ['userWallets', selectedUser?._id],
    queryFn: () => walletAPI.getWalletsByUser(selectedUser._id).then(r => r.data),
    enabled: !!isOpen && !!selectedUser?._id
  });

  if (!isOpen || !selectedUser) return null;

  const handleDownloadDocument = async (docType) => {
    try {
      const response = await usersAPI.downloadDocument(selectedUser._id, docType);

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedUser.documents[docType] || `${docType}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Eye className="h-6 w-6 text-primary-600 mr-3" />
            <h3 className="text-xl font-semibold" style={{color: "#6b7280"}}>
              User Details
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 h-6 w-6"
          >
            <X className="h-6 w-6" style={{ color: '#9ca3af' }} />
          </button>
        </div>

        <div className="space-y-8">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-secondary-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-dark-gray">User Name</label>
                <p className="text-sm text-secondary-900">{getUserDisplayName(selectedUser)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-dark-gray">Status</label>
                <div className="mt-1">{getStatusBadge(selectedUser)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-dark-gray">Created At</label>
                <p className="text-sm text-secondary-900">
                  {new Date(selectedUser.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-dark-gray">Email</label>
                <p className="text-sm text-secondary-900">{selectedUser.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-dark-gray">Password</label>
                <p className="text-sm text-secondary-900">••••••••</p>
              </div>
            </div>
          </div>

          {/* Corporate Information */}
          {selectedUser.corporateInfo && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-secondary-900 mb-4">Corporate Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700">Legal Entity Name</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.legalEntityName || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Business Registration Number</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.businessRegistrationNumber || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Registration Type</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.registrationType || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Date of Incorporation</label>
                  <p className="text-sm text-secondary-900">
                    {selectedUser.corporateInfo.dateOfIncorporation
                      ? new Date(selectedUser.corporateInfo.dateOfIncorporation).toLocaleDateString()
                      : '—'
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Registered Address</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.registeredAddress || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Operating Address</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.operatingAddress || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Phone Number</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.phoneNumber || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Website</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.website || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Business Nature</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.businessNature || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Tax ID Number</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.taxIdNumber || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Annual Revenue Range</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.annualRevenue || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700">Number of Employees</label>
                  <p className="text-sm text-secondary-900">{selectedUser.corporateInfo.numberOfEmployees || '—'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Documents Section */}
          {selectedUser.documents && Object.keys(selectedUser.documents).some(key => selectedUser.documents[key]) && (
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-secondary-900 mb-4">Documents</h4>

              {/* Business Documents */}
              <div className="mb-4">
                <h5 className="text-md font-medium text-secondary-800 mb-2">Business Documents</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {['ssmProfile', 'memorandumArticles', 'boardResolution', 'proofOfAddress', 'financialStatements'].map(docType => (
                    selectedUser.documents[docType] && (
                      <div key={docType} className="flex items-center justify-between bg-white p-3 rounded border hover:bg-gray-50 transition-colors">
                        <span className="text-sm text-secondary-700 flex-1">{getDocumentDisplayName(docType)}</span>
                        <button
                          onClick={() => handleDownloadDocument(docType)}
                          className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100 hover:border-primary-300 transition-colors"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </button>
                      </div>
                    )
                  ))}
                </div>
              </div>

              {/* Membership Documents */}
              <div>
                <h5 className="text-md font-medium text-secondary-800 mb-2">Membership Documents</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {['participationCertificate', 'officialMemberRegistry', 'operationalProcedures', 'authorizedSignatories', 'technicalConnectivity', 'feePaymentReceipts', 'formalMembershipApproval'].map(docType => (
                    selectedUser.documents[docType] && (
                      <div key={docType} className="flex items-center justify-between bg-white p-3 rounded border hover:bg-gray-50 transition-colors">
                        <span className="text-sm text-secondary-700 flex-1">{getDocumentDisplayName(docType)}</span>
                        <button
                          onClick={() => handleDownloadDocument(docType)}
                          className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100 hover:border-primary-300 transition-colors"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </button>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Authorized Representatives */}
          {selectedUser.representatives && selectedUser.representatives.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-secondary-900 mb-4">Authorized Representatives</h4>
              <div className="space-y-4">
                {selectedUser.representatives.map((rep, index) => (
                  <div key={index} className="bg-white p-3 rounded border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-medium text-secondary-600">Full Name</label>
                        <p className="text-sm text-secondary-900">{rep.fullName || '—'}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-secondary-600">Email Address</label>
                        <p className="text-sm text-secondary-900">{rep.email || '—'}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-secondary-600">Phone Number</label>
                        <p className="text-sm text-secondary-900">{rep.phoneNumber || '—'}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-secondary-600">NRIC/Passport Number</label>
                        <p className="text-sm text-secondary-900">{rep.nricPassport || '—'}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-secondary-600">Position</label>
                        <p className="text-sm text-secondary-900">{rep.position || '—'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Associated Wallets */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-secondary-900 mb-4 flex items-center">Associated Wallets</h4>
            {walletsLoading && (
              <div className="text-sm text-secondary-600">Loading wallets...</div>
            )}
            {walletsError && (
              <div className="text-sm text-error-600">Failed to load wallets: {walletsErrorObj?.message || 'Unknown error'}</div>
            )}
            {!walletsLoading && !walletsError && (
              <>
                {(!walletsData?.wallets || walletsData.wallets.length === 0) && (
                  <div className="text-sm text-secondary-600">No wallets found for this user.</div>
                )}
                {walletsData?.wallets?.length > 0 && (
                  <div className="space-y-4">
                    {walletsData.wallets.map(w => (
                      <div key={w._id} className="bg-white rounded border p-4 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs font-medium text-secondary-600">Wallet Address</label>
                              {w.walletAddress && (
                                <button
                                  type="button"
                                  onClick={() => { navigator.clipboard.writeText(w.walletAddress); toast.success('Address copied'); }}
                                  className="text-primary-600 hover:text-primary-800 p-1 rounded hover:bg-primary-50"
                                  aria-label="Copy wallet address"
                                  title="Copy wallet address"
                                >
                                  <CopyIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            <p className="text-sm font-mono break-all">{w.walletAddress || '—'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-secondary-600">Network</label>
                            <p className="text-sm text-secondary-900">{w.network || '—'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-secondary-600">Status</label>
                            <p className="text-sm text-secondary-900 capitalize">{w.status || '—'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-secondary-600">Wallet Type</label>
                            <p className="text-sm text-secondary-900 capitalize">{w.walletType || '—'}</p>
                          </div>
                          {w.bankAccountDetails && (
                            <div className="md:col-span-2 border-t pt-3 mt-2">
                              <h5 className="text-sm font-medium text-secondary-800 mb-2">Bank Account Details</h5>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                <div><span className="font-semibold">Type:</span> {w.bankAccountDetails.accountType || '—'}</div>
                                <div><span className="font-semibold">Bank:</span> {w.bankAccountDetails.bankName || '—'}</div>
                                <div><span className="font-semibold">Account Name:</span> {w.bankAccountDetails.accountName || '—'}</div>
                                <div><span className="font-semibold">Account No:</span> {w.bankAccountDetails.accountNumber || '—'}</div>
                                <div><span className="font-semibold">Category:</span> {w.bankAccountDetails.accountCategory || '—'}</div>
                                {w.bankAccountDetails.swiftBic && <div><span className="font-semibold">SWIFT/BIC:</span> {w.bankAccountDetails.swiftBic}</div>}
                                {w.bankAccountDetails.branchCode && <div><span className="font-semibold">Branch:</span> {w.bankAccountDetails.branchCode}</div>}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 text-[11px] text-secondary-500 flex flex-wrap gap-4">
                          <span>Created: {w.createdAt ? new Date(w.createdAt).toLocaleString() : '—'}</span>
                          <span>Last Activity: {w.lastActivity ? new Date(w.lastActivity).toLocaleString() : '—'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Transaction History section removed as per request */}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="btn btn-primary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;